import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListZones } from 'src/features/zone/api/useListZones';
import CreateGatewayFormModal from 'src/features/gateway/components/form/CreateGatewayFormModal';
import CellGatewayActions from 'src/features/gateway/components/cell/CellGatewayActions';
import { Gateway } from 'src/features/gateway/utils/types';
import { requiredIdentifiersTranslationMap } from 'src/features/gateway/utils/constants';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import { generalMessages } from 'src/utils/messages';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const columnHelper = createColumnHelper<Gateway>();
function GatewaysTable() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageGateways = useHasPermissions([PERMISSION.MANAGE_GATEWAYS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'name', desc: false }] });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, parkingId, queryPaginationOptions],
    );

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(gatewayMessages.gateways));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: gatewaysData, isLoading, isError } = useListGateways(payload);
    const { data: zonesData } = useListZones({
        parkingId,
    });

    const zoneOptions = React.useMemo(
        () =>
            zonesData?.results.map((zone) => ({
                label: zone?.translations.find((translation) => translation.language === locale)?.name,
                value: zone.id,
            })),
        [zonesData, locale],
    );

    const requiredIdentifiersOptions = Object.entries(requiredIdentifiersTranslationMap).map(([key, value]) => ({
        label: formatMessage(value),
        value: key,
    }));

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => row.original.translations.find((translation) => translation.language === locale)?.name,
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('entry_zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: ({ row }) => {
                const entryZoneId = row.original?.entry_zone?.id;
                const zone = zonesData?.results.find((zone) => zone.id === entryZoneId);

                return zone?.translations.find((translation) => translation.language === locale)?.name || '-';
            },
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(gatewayMessages.entryZone),
                options: zoneOptions,
            },
            id: 'entry_zone_name',
        }),
        columnHelper.accessor('exit_zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: ({ row }) => {
                const exitZoneId = row.original?.exit_zone?.id;
                const zone = zonesData?.results.find((zone) => zone.id === exitZoneId);

                return zone?.translations.find((translation) => translation.language === locale)?.name || '-';
            },
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(gatewayMessages.exitZone),
                options: zoneOptions,
            },
            id: 'exit_zone_name',
        }),
        columnHelper.accessor('required_identifier_types', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.required_identifier_types
                    .map((type) => formatMessage(requiredIdentifiersTranslationMap[type]))
                    .join(', '),
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(gatewayMessages.requiredIdentifiers),
                options: requiredIdentifiersOptions,
            },
        }),
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.translations.find((translation) => translation.language === locale)?.description || '-',
            meta: {
                title: formatMessage(generalMessages.description),
            },
            id: 'description',
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellGatewayActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Gateway, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={gatewaysData}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(gatewayMessages.gateways),
                entryName: formatMessage(gatewayMessages.gatewayPoint).toLowerCase(),
                entryNames: formatMessage(gatewayMessages.gatewayPoints).toLowerCase(),
                placeholder: formatMessage(generalMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageGateways && { newEntry: CreateGatewayFormModal }),
            }}
        />
    );
}

export default GatewaysTable;
